/**
 * 塩基色の解決（§0.3.4・U165）。
 * A=緑 / C=青 / G=黄（既定。初期 Dye Set の黒に切替可）/ T=赤 / N=灰。
 * 文字色として使う。背景のベタ塗りには使わない。
 */

import {
  BASE_COLORS_DARK,
  BASE_COLORS_LIGHT,
  BASE_COLORS_LIGHT_AA,
  BaseColorPalette,
  GUANINE_COLOR_BLACK,
  GUANINE_COLOR_BLACK_DARK,
} from '../constants';
import { CODE_TO_CHAR, NUCLEOTIDE_CODE_COUNT } from '../sequence/nucleotideCodes';

export type BaseColorTheme = 'light' | 'dark';
/** G の色。`yellow` が既定、`black` は初期 Dye Set 準拠 */
export type GuanineStyle = 'yellow' | 'black';
/**
 * `spec` = §0.3.4 で確定した色そのまま（既定）。
 * `aa` = 明背景で WCAG 2.1 AA（4.5:1）を満たす同色相版（§0.3.3）。
 * 暗背景は両者とも AA を満たすため同じ。
 */
export type BaseColorContrast = 'spec' | 'aa';

export interface BaseColorOptions {
  theme?: BaseColorTheme;
  guanine?: GuanineStyle;
  contrast?: BaseColorContrast;
}

export function resolveBaseColors(options: BaseColorOptions = {}): BaseColorPalette {
  const theme = options.theme ?? 'light';
  const contrast = options.contrast ?? 'spec';
  const source =
    theme === 'dark' ? BASE_COLORS_DARK : contrast === 'aa' ? BASE_COLORS_LIGHT_AA : BASE_COLORS_LIGHT;
  const palette: BaseColorPalette = { ...source };
  if (options.guanine === 'black') {
    palette.G = theme === 'dark' ? GUANINE_COLOR_BLACK_DARK : GUANINE_COLOR_BLACK;
  }
  return palette;
}

/** 明背景の既定色が AA を満たさない塩基（UI の設定説明に使う） */
export function basesBelowAaOnLight(background = '#FFFFFF'): string[] {
  const palette = resolveBaseColors();
  return (Object.keys(palette) as Array<keyof BaseColorPalette>).filter(
    (base) => contrastRatio(palette[base], background) < 4.5
  );
}

/** 塩基コード → 色。描画ループから引けるよう配列で返す。 */
export function baseColorTable(options: BaseColorOptions = {}): string[] {
  const palette = resolveBaseColors(options);
  const table = new Array<string>(NUCLEOTIDE_CODE_COUNT);
  for (let code = 0; code < NUCLEOTIDE_CODE_COUNT; code += 1) {
    table[code] = palette[CODE_TO_CHAR[code] as keyof BaseColorPalette];
  }
  return table;
}

// ------------------------------------------------------------ コントラスト

function channelToLinear(value: number): number {
  const ratio = value / 255;
  return ratio <= 0.04045 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return 0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b);
}

/** WCAG 2.1 のコントラスト比（§0.3.3・U163） */
export function contrastRatio(foreground: string, background: string): number {
  const a = relativeLuminance(foreground);
  const b = relativeLuminance(background);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}
